import {
  LayoutGrid,
  ListFilter,
  Plus,
  List,
  LayoutList,
  UserRoundPlus,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { SearchInput } from "@/components/ui/search-input";
import { FilterState, ProjectLabel, ProjectUser } from "./types";
import {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { IconUser } from "../ui/icons";
import { cn } from "@/lib/utils";
import { UserDropdown } from "./UserDropdown";
import * as Checkbox from "@radix-ui/react-checkbox";
import { LabelDropdown } from "./LabelDropdown";

import { FilterMenu } from "./FilterMenu";
import CreateCanvasModal from "@/components/CreateCanvasModal";
import { KanbanDefaultStates, columnStatuses } from "./CanvasKanban";
import { useProjectModalStore } from "@/store/zustand/projectModalStore";
import { useCreateCanvasModal } from "@/hooks/useCreateCanvasModal";
import { useShortcuts } from "../providers/ShortcutProvider";
import { CommandPalette } from "./CommandPalette";
import { useSelector } from "react-redux";
import { FeaturePrd } from "@/store/zustand/supabaseStore";
import { CommandPaletteOption } from "@/store/zustand/commandPaletteStore";
import { OptionCategory, useCommandPalette } from "@/hooks/useCommandPalette";
import { InviteModal } from "./InviteModal";
import { getAltKey, getModifierKey } from "@/utils/utils";

interface Props {
  filters: FilterState;
  setFilters: Dispatch<SetStateAction<FilterState>>;
  project: any;
  setViewType: Dispatch<SetStateAction<"grid" | "list">>;
  viewType: "grid" | "list";
}

interface UserFilterProps {
  users: Array<ProjectUser>;
  selectedUsers: Array<string>;
  onChange: (ids: Array<string>) => void;
}

function UsersFilter({ users, selectedUsers, onChange }: UserFilterProps) {
  const [usersToShow, setUsersToShow] = useState<Array<ProjectUser>>(
    users?.slice(0, 2)
  );

  //Sort the array to show the selected avatars first
  useEffect(() => {
    if (selectedUsers.length) {
      const sortedArray = users?.sort((a, b) => {
        const indexA = selectedUsers.indexOf(a.id);
        const indexB = selectedUsers.indexOf(b.id);

        if (indexA === -1 && indexB === -1) {
          return 0;
        } else if (indexA === -1) {
          return 1;
        } else if (indexB === -1) {
          return -1;
        } else {
          return indexA - indexB;
        }
      });
      setUsersToShow(sortedArray?.slice(0, 2) || []);
    } else {
      setUsersToShow(users?.slice(0, 2) || []);
    }
  }, [selectedUsers, users]);

  const renderUserIcon = useCallback(
    (user: ProjectUser) => {
      return user.picture ? (
        <img
          src={user.picture}
          alt={user.name}
          className={cn(
            "size-6 border-2 border-secondary-n-00 shadow-md rounded-md bg-avatar",
            selectedUsers.includes(user.id) ? "border-primary-n-600" : undefined
          )}
          crossOrigin="anonymous"
        />
      ) : (
        <div
          className={cn(
            "flex bg-secondary-n-1000 items-center justify-center size-6 rounded-md border-2 border-secondary-n-600",
            selectedUsers.includes(user.id) ? "border-primary-n-600" : undefined
          )}
        >
          <IconUser />
        </div>
      );
    },
    [selectedUsers]
  );

  const dropdownTrigger = useMemo(
    () => (
      <div className="flex gap-1 items-center cursor-pointer">
        <div className="flex">
          {usersToShow?.map((user) => (
            <div
              key={user?.id}
              title={user?.name ?? user?.email}
              className={
                usersToShow.length > 1
                  ? "last:-translate-x-1 first:z-0 last:z-10 shadow-md"
                  : undefined
              }
            >
              {renderUserIcon(user)}
            </div>
          ))}
        </div>
        {users?.length > 2 && (
          <span
            className={cn(
              "p-0.5 text-secondary-n-600 text-xs font-medium rounded-md border-2 border-transparent",
              selectedUsers.length > 2
                ? "bg-primary-n-900 bg-opacity-25 text-secondary-n-400"
                : undefined
            )}
          >
            +{users.length - 2}
          </span>
        )}
      </div>
    ),
    [usersToShow, selectedUsers]
  );

  return (
    <UserDropdown
      triggerElement={dropdownTrigger}
      users={users}
      onChange={onChange}
      title="Filter by user"
      multiple
      clearString="Clear user filter"
    />
  );
}

interface LabelFiltersProps {
  labels: Array<ProjectLabel>;
  selectedLabels: Array<string>;
  onChange: (ids: Array<string>) => void;
}

function LabelsFilter({ labels, selectedLabels, onChange }: LabelFiltersProps) {
  const filterTriggerButton = useMemo(() => {
    const selectedLabelsObj =
      labels?.filter((label: any) => selectedLabels?.includes(label.id)) || [];
    return (
      <div className="flex gap-2 cursor-pointer">
        {selectedLabelsObj?.length === 1 && (
          <div
            key={selectedLabelsObj[0].id}
            className="flex gap-2 items-center text-secondary-n-400 rounded-lg bg-secondary-n-950 px-2"
          >
            {selectedLabelsObj[0].color && (
              <Checkbox.Root
                className="w-2.5 h-2.5 border rounded-xs flex items-center justify-center"
                style={{
                  borderColor: selectedLabelsObj[0].color?.value || "#000",
                }}
                defaultChecked
              >
                <Checkbox.Indicator
                  className="w-full h-full flex items-center justify-center"
                  style={{
                    backgroundColor:
                      selectedLabelsObj[0].color?.value || "#000",
                  }}
                ></Checkbox.Indicator>
              </Checkbox.Root>
            )}
            <span className="text-xs">{selectedLabelsObj[0].label}</span>
          </div>
        )}
        {selectedLabelsObj.length > 1 && (
          <div className="flex gap-2 items-center text-secondary-n-400 rounded-lg bg-secondary-n-950 px-2">
            {selectedLabelsObj.slice(0, 3).map(
              (label: any, index: number) =>
                label.color && (
                  <Checkbox.Root
                    key={label.id}
                    className={`w-2.5 h-2.5 border rounded-xs flex items-center justify-center ${
                      index > 0 ? "-ml-1.5" : ""
                    }`}
                    style={{ borderColor: label?.color?.value || "#000" }}
                    defaultChecked
                  >
                    <Checkbox.Indicator
                      className="w-full h-full flex items-center justify-center"
                      style={{ backgroundColor: label?.color?.value || "#000" }}
                    ></Checkbox.Indicator>
                  </Checkbox.Root>
                )
            )}
            <span className="text-xs">{selectedLabelsObj.length} labels</span>
          </div>
        )}
        <Button
          className={cn(
            "h-9 flex items-center gap-1 px-2 text-sm font-medium ring-1 ring-inset ring-secondary-n-800 bg-background hover:bg-secondary-n-950 transition-colors"
          )}
          variant="secondary"
        >
          <ListFilter className="size-4" />
          {!selectedLabels.length && (
            <p className="text-sm font-medium">Filter</p>
          )}
        </Button>
      </div>
    );
  }, [selectedLabels, labels]);

  return (
    <LabelDropdown
      triggerElement={filterTriggerButton}
      labels={labels}
      title="Filter by label"
      clearString="Clear label filter"
      onChange={onChange}
      addNew={false}
    />
  );
}

interface FilterMenuHandle {
  clearFilters: () => void;
  openMenu: () => void;
}

export function ProjectPageHeader({
  filters,
  setFilters,
  project,
  setViewType,
  viewType,
}: Props) {
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showCommandPalette, setShowCommandPalette] = useState(false);
  const { isOpen, setIsOpen } = useCreateCanvasModal();
  const filterMenuRef = useRef<FilterMenuHandle>(null);
  const { registerShortcut } = useShortcuts();

  // Get canvas previews to calculate createIndex
  const canvasPreviews = useSelector(
    (state: any) => state.supabaseStore.canvasPreviews
  );
  const featurePrds = useSelector(
    (state: any) => state.supabaseStore.featurePrds
  );

  // Calculate stateToCanvases
  const stateToCanvases = useMemo(() => {
    if (!project) return {};

    // Get all canvases for the current project
    const projectCanvases = (
      canvasPreviews ? Object.values(canvasPreviews) : []
    ).filter((canvas: any) => canvas.project_id === project.id);

    // Get all PRDs for these canvases
    const canvasIdToPrd: { [key: string]: FeaturePrd } = {};
    if (featurePrds) {
      Object.values(featurePrds).forEach((prd: any) => {
        canvasIdToPrd[prd.canvas_id] = prd;
      });
    }

    // Initialize columns with empty canvas arrays
    const stateToCanvases = {};
    Object.keys(KanbanDefaultStates).forEach((key) => {
      stateToCanvases[KanbanDefaultStates[key]] = {
        stateKey: key,
        title: key,
        canvases: [],
      };
    });

    // Group canvases into their respective columns
    projectCanvases.forEach((canvas: any) => {
      const prd = canvasIdToPrd[canvas.id as string];
      const status = prd?.status || KanbanDefaultStates.BACKLOG;
      stateToCanvases[status]?.canvases.push(canvas);
    });

    return stateToCanvases;
  }, [project, canvasPreviews, featurePrds]);

  // Command palette options
  const globalCommandPaletteOptions = useMemo<CommandPaletteOption[]>(
    () =>
      [
        {
          icon: <ListFilter className="size-4" />,
          name: "Clear All Filters",
          shortcut: [getAltKey(), "shift", "F"],
          registerKeys: "alt+shift+f",
          action: () => filterMenuRef.current?.clearFilters(),
          hasFollowUp: false,
        },
        {
          icon: <Plus className="size-4" />,
          name: "Create Canvas",
          shortcut: ["C"],
          registerKeys: "c",
          action: () => setIsOpen("BACKLOG"),
          preventDefault: true,
          hasFollowUp: true,
        },
        {
          icon: <ListFilter className="size-4" />,
          name: "Open Filters",
          shortcut: ["F"],
          registerKeys: "f",
          action: () => filterMenuRef.current?.openMenu(),
          preventDefault: true,
          hasFollowUp: true,
        },
        {
          icon:
            viewType === "grid" ? (
              <LayoutList className="size-4" />
            ) : (
              <LayoutGrid className="size-4" />
            ),
          name: `Switch to ${viewType === "grid" ? "List" : "Grid"} View`,
          shortcut: [getModifierKey(), "B"],
          registerKeys: "mod+b",
          action: () => setViewType(viewType === "grid" ? "list" : "grid"),
          preventDefault: true,
          hasFollowUp: false,
        },
      ].sort((a, b) => a.name.localeCompare(b.name)),
    [viewType, setViewType]
  );

  // Register all shortcuts
  useEffect(() => {
    globalCommandPaletteOptions.forEach((option) => {
      registerShortcut(
        option.registerKeys,
        (e) => {
          e.preventDefault();
          option.action?.();
        },
        { preventDefault: option.preventDefault }
      );
    });
  }, [registerShortcut, globalCommandPaletteOptions]);

  const { registerCategory, open, showCategory } = useCommandPalette();

  useEffect(() => {
    registerCategory(OptionCategory.GLOBAL, globalCommandPaletteOptions);
    showCategory(OptionCategory.GLOBAL);
  }, [globalCommandPaletteOptions]);

  // Register command palette shortcut separately since it's not in the options
  useEffect(() => {
    registerShortcut(
      "mod+k",
      (e) => {
        e.preventDefault();
        open();
      },
      { preventDefault: true }
    );
  }, [registerShortcut]);

  // Extract users and labels from project
  const users = useMemo(() => {
    return Array.isArray(project?.users) ? project.users : [];
  }, [project?.users]);
  const labels = useMemo(() => project?.labels || [], [project?.labels]);

  const handleSearch = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setFilters((prev: FilterState) => {
        return {
          ...prev,
          search: event?.target?.value as string,
        } as FilterState;
      });
    },
    [setFilters]
  );

  const handleUserFilterChange = useCallback(
    (userIds: Array<string>) => {
      setFilters((prev: FilterState) => {
        return {
          ...prev,
          userIds,
        } as FilterState;
      });
    },
    [setFilters]
  );

  const handleLabelsFilterChange = useCallback(
    (labels: Array<string>) => {
      setFilters((prev: FilterState) => {
        return {
          ...prev,
          labels,
        } as FilterState;
      });
    },
    [setFilters]
  );

  const handleFiltersChange = useCallback(
    (selectedFilters: Set<string>) => {
      // Handle status filters
      const statuses = Array.from(selectedFilters)
        .filter((filter) => filter.startsWith("status-"))
        .map((filter) => filter.replace("status-", ""));

      // Handle label filters
      const labels = Array.from(selectedFilters)
        .filter((filter) => filter.startsWith("label-"))
        .map((filter) => filter.replace("label-", ""));

      // Handle assignee filters
      const userIds = Array.from(selectedFilters)
        .filter((filter) => filter.startsWith("assignee-"))
        .map((filter) => filter.replace("assignee-", ""));

      // Handle estimate filters
      const estimates = Array.from(selectedFilters)
        .filter((filter) => filter.startsWith("estimate-"))
        .map((filter) => {
          const value = filter.replace("estimate-", "");
          return value === "none" ? 0 : parseInt(value, 10);
        });

      // Handle priority filters
      const priorities = Array.from(selectedFilters)
        .filter((filter) => filter.startsWith("priority-"))
        .map((filter) => {
          const value = filter.replace("priority-", "");
          return value === "none" ? null : parseInt(value, 10);
        })
        .filter((value): value is number => value !== null);

      // Handle due date filters
      const dueDates = {
        none: selectedFilters.has("due-none"),
        overdue: selectedFilters.has("due-overdue"),
        today: selectedFilters.has("due-today"),
        thisWeek: selectedFilters.has("due-this-week"),
        nextWeek: selectedFilters.has("due-next-week"),
        later: selectedFilters.has("due-later"),
      };

      setFilters((prev: FilterState) => ({
        ...prev,
        statuses,
        labels,
        userIds,
        estimates,
        priorities,
        dueDates,
      }));
    },
    [setFilters]
  );

  const handleFilterMenuOpenChange = useCallback((open: boolean) => {
    if (open) {
      useProjectModalStore.getState().addProjectModal("filter-menu");
    } else {
      useProjectModalStore.getState().removeProjectModal("filter-menu");
    }
  }, []);

  return (
    <div className="flex flex-col gap-4 w-full border-b border-secondary-n-950">
      <div className="flex items-center justify-between border-b border-secondary-n-900 min-w-0 p-2">
        <div className="flex items-center gap-3">
          <div className="w-72">
            <SearchInput onChange={handleSearch} placeholder="Search" />
          </div>
        </div>

        {/* Right side - Controls */}
        <div className="flex items-center gap-2 ml-auto">
          <FilterMenu
            ref={filterMenuRef}
            users={users}
            labels={labels}
            statuses={columnStatuses}
            onFiltersChange={handleFiltersChange}
            className="rounded-md"
            onOpenChange={handleFilterMenuOpenChange}
          />
          <div className="flex items-center h-7 gap-0.5 ring-inset ring-secondary-n-800 bg-secondary-n-950 rounded-md p-1 py-2">
            <Button
              onClick={() => setViewType("grid")}
              className={cn(
                "flex items-center px-1.5 hover:bg-background-tertiary transition-colors rounded-md",
                viewType === "grid" && "bg-background-tertiary text-white"
              )}
              variant="ghost"
              size="xs"
            >
              <LayoutGrid className="size-4" />
            </Button>
            <Button
              onClick={() => setViewType("list")}
              className={cn(
                "flex items-center px-1.5 hover:bg-background-tertiary transition-colors rounded-md",
                viewType === "list" && "bg-background-tertiary text-white"
              )}
              variant="ghost"
              size="xs"
            >
              <List className="size-4" />
            </Button>
          </div>
          <div className="border-r h-7 border-secondary-n-900">{""}</div>
          <Button
            onClick={() => setShowInviteModal(true)}
            className="h-7 flex items-center gap-1"
            variant="secondary"
          >
            <UserRoundPlus className="size-4" />
            <p className="text-xs">Invite</p>
          </Button>
        </div>
        <InviteModal
          open={showInviteModal}
          setOpen={setShowInviteModal}
          projectId={project?.id}
        />
        <CommandPalette />
        <CreateCanvasModal
          openCanvasState={isOpen ? KanbanDefaultStates[isOpen] : isOpen}
          setOpenCanvasState={(state: string | null) => {
            setIsOpen(null);
            useProjectModalStore
              .getState()
              .removeProjectModal("create-canvas-modal");
          }}
          project={project}
          createIndex={
            typeof isOpen === "string" && KanbanDefaultStates[isOpen]
              ? stateToCanvases?.[KanbanDefaultStates[isOpen]]?.canvases
                  ?.length || 0
              : 0
          }
        />
      </div>
    </div>
  );
}
